import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  useGetPendingCoursesQuery,
  useUpdateCourseStatusMutation,
} from '../../api/apiSlice';
import {
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff,
  Clock3,
  Loader2,
  AlertCircle,
  User,
  Tag,
  DollarSign,
  Layers3,
  CalendarDays,
  FileText,
  BookOpen,
} from 'lucide-react';

const AdminCourseManagement = () => {
  const { t, i18n } = useTranslation();
  const { data: courses, isLoading, error, refetch } = useGetPendingCoursesQuery();
  const [updateCourseStatus, { isLoading: isUpdating }] = useUpdateCourseStatusMutation();
  const [rejectionReason, setRejectionReason] = useState({});
  const [expandedCourse, setExpandedCourse] = useState(null);

  const pendingCourses = courses || [];
  const locale = i18n.resolvedLanguage === 'kk' ? 'kk-KZ' : 'ru-RU';

  const handleApprove = async (courseId) => {
    const toastId = toast.loading(t('admin.approving'));

    try {
      await updateCourseStatus({ courseId, status: 'active' }).unwrap();
      toast.success(t('admin.approved'), { id: toastId });
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || t('admin.approveError'), {
        id: toastId,
      });
      console.error('Approve error:', err);
    }
  };

  const handleReject = async (courseId) => {
    const reason = rejectionReason[courseId] || '';

    if (!reason.trim()) {
      toast.error(t('admin.rejectionReasonRequired'));
      return;
    }

    const toastId = toast.loading(t('admin.rejecting'));

    try {
      await updateCourseStatus({
        courseId,
        status: 'rejected',
        rejectionReason: reason,
      }).unwrap();

      toast.success(t('admin.rejected'), { id: toastId });
      setRejectionReason((prev) => ({ ...prev, [courseId]: '' }));
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || t('admin.rejectError'), {
        id: toastId,
      });
      console.error('Reject error:', err);
    }
  };

  const toggleExpand = (courseId) => {
    setExpandedCourse((prev) => (prev === courseId ? null : courseId));
  };

  const formatPrice = (price) => {
    if (!price || Number(price) === 0) return t('common.free');
    return `${Number(price).toLocaleString(locale)} ${t('common.currency')}`;
  };

  const cardClass =
      'rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white dark:bg-gray-800 shadow-sm';
  const mutedCardClass =
      'rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40';
  const inputClass =
      'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 px-4 py-3 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 resize-none';
  const actionBtnClass =
      'inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed';

  if (isLoading) {
    return (
        <div className="flex min-h-[60vh] items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="flex min-h-[60vh] items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
          <div className="max-w-md w-full rounded-2xl border border-red-200 dark:border-red-900/40 bg-white dark:bg-gray-900 p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-950/30">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('errors.generic')}</h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {error?.data?.message || error?.message || t('admin.loadError')}
            </p>
          </div>
        </div>
    );
  }

  return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                {t('admin.moderationTitle')}
              </h1>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('admin.moderationSubtitle')}
              </p>
            </div>

            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('admin.totalPending')}: <span className="font-semibold text-gray-900 dark:text-white">{pendingCourses.length}</span>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <div className={`${cardClass} p-5`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.totalRequests')}</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                    {pendingCourses.length}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-950/30">
                  <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>

            <div className={`${cardClass} p-5`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.underReview')}</p>
                  <p className="mt-1 text-2xl font-bold text-amber-600 dark:text-amber-400">
                    {pendingCourses.length}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-950/30">
                  <Clock3 className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
            </div>

            <div className={`${cardClass} p-5 sm:col-span-2 xl:col-span-1`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.statusLabel')}</p>
                  <p className="mt-1 text-base font-semibold text-gray-900 dark:text-white">
                    {t('admin.statusValue')}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-950/30">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={cardClass}>
            {pendingCourses.length === 0 ? (
                <div className="px-6 py-20 text-center sm:px-8">
                  <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 dark:bg-emerald-950/30">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {t('admin.allChecked')}
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.allCheckedDesc')}
                  </p>
                </div>
            ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {pendingCourses.map((course) => {
                    const isExpanded = expandedCourse === course._id;

                    return (
                        <div key={course._id} className="p-5 sm:p-6">
                          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                            {/* Left */}
                            <div className="min-w-0 flex-1">
                              <div className="flex gap-4">
                                {course.coverImageUrl ? (
                                    <img
                                        src={course.coverImageUrl}
                                        alt={course.title}
                                        className="h-20 w-20 rounded-2xl object-cover border border-gray-200 dark:border-gray-700 shrink-0"
                                    />
                                ) : (
                                    <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-lg font-bold text-white">
                                      {course.title?.slice(0, 2)?.toUpperCase() || 'CR'}
                                    </div>
                                )}

                                <div className="min-w-0 flex-1">
                                  <div className="mb-2 flex flex-wrap items-center gap-2">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                      {course.title}
                                    </h3>

                                    <span className="inline-flex items-center rounded-full bg-amber-50 dark:bg-amber-950/30 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                                {t('course.status.pending_review')}
                              </span>
                                  </div>

                                  <div className="mb-3 grid grid-cols-1 gap-2 text-sm text-gray-500 dark:text-gray-400 sm:grid-cols-2 lg:grid-cols-4">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <User className="h-4 w-4 shrink-0" />
                                      <span className="truncate">
                                  {course.instructor?.name ||
                                      course.instructor?.email ||
                                      t('course.instructorMissing')}
                                </span>
                                    </div>

                                    <div className="flex items-center gap-2 min-w-0">
                                      <Tag className="h-4 w-4 shrink-0" />
                                      <span className="truncate">
                                  {course.category?.title || t('course.noCategory')}
                                </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <DollarSign className="h-4 w-4 shrink-0" />
                                      <span className="font-medium text-emerald-600 dark:text-emerald-400">
                                  {formatPrice(course.price)}
                                </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <CalendarDays className="h-4 w-4 shrink-0" />
                                      <span>
                                  {course.createdAt
                                      ? new Date(course.createdAt).toLocaleDateString(locale)
                                      : '—'}
                                </span>
                                    </div>
                                  </div>

                                  {course.description && (
                                      <p className="line-clamp-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                                        {course.description}
                                      </p>
                                  )}

                                  {isExpanded && (
                                      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                                        <div className={`${mutedCardClass} p-4`}>
                                          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                                            <FileText className="h-4 w-4" />
                                            {t('admin.courseInfo')}
                                          </div>

                                          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                                            <div className="flex items-center justify-between gap-3">
                                              <span>{t('course.language')}</span>
                                              <span className="font-medium text-gray-900 dark:text-white">
                                        {course.language || '—'}
                                      </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3">
                                              <span>{t('course.sections')}</span>
                                              <span className="font-medium text-gray-900 dark:text-white">
                                        {course.sections?.length || 0}
                                      </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3">
                                              <span>{t('admin.statusLabel')}</span>
                                              <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                                        {t('course.status.pending_review')}
                                      </span>
                                            </div>
                                          </div>
                                        </div>

                                        {course.rejectionReason && (
                                            <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4">
                                              <div className="mb-2 text-sm font-medium text-red-800 dark:text-red-300">
                                                {t('admin.previousRejection')}
                                              </div>
                                              <p className="text-sm leading-6 text-red-700 dark:text-red-200">
                                                {course.rejectionReason}
                                              </p>
                                            </div>
                                        )}
                                      </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Right */}
                            <div className="w-full xl:w-[340px]">
                              <div className="space-y-3">
                          <textarea
                              placeholder={t('admin.rejectionReasonPlaceholder')}
                              value={rejectionReason[course._id] || ''}
                              onChange={(e) =>
                                  setRejectionReason((prev) => ({
                                    ...prev,
                                    [course._id]: e.target.value,
                                  }))
                              }
                              rows={4}
                              className={inputClass}
                          />

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-1">
                                  <Link
                                      to={`/dashboard/admin/course-management/${course._id}`}
                                      className={`${actionBtnClass} bg-indigo-600 text-white hover:bg-indigo-700`}
                                  >
                                    <Eye className="h-4 w-4" />
                                    {t('admin.openFullPreview')}
                                  </Link>

                                  <button
                                      onClick={() => toggleExpand(course._id)}
                                      className={`${actionBtnClass} border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800`}
                                  >
                                    {isExpanded ? (
                                        <>
                                          <EyeOff className="h-4 w-4" />
                                          {t('common.less')}
                                        </>
                                    ) : (
                                        <>
                                          <Eye className="h-4 w-4" />
                                          {t('common.more')}
                                        </>
                                    )}
                                  </button>

                                  <button
                                      onClick={() => handleApprove(course._id)}
                                      disabled={isUpdating}
                                      className={`${actionBtnClass} bg-emerald-600 text-white hover:bg-emerald-700`}
                                  >
                                    {isUpdating ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <CheckCircle2 className="h-4 w-4" />
                                    )}
                                    {t('admin.approve')}
                                  </button>

                                  <button
                                      onClick={() => handleReject(course._id)}
                                      disabled={isUpdating || !rejectionReason[course._id]?.trim()}
                                      className={`${actionBtnClass} bg-red-600 text-white hover:bg-red-700`}
                                  >
                                    {isUpdating ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <XCircle className="h-4 w-4" />
                                    )}
                                    {t('admin.reject')}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                    );
                  })}
                </div>
            )}
          </div>

          {pendingCourses.length > 0 && (
              <div className="mt-5 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('admin.totalPending')}: <span className="font-medium text-gray-700 dark:text-gray-200">{pendingCourses.length}</span>
                </p>
              </div>
          )}
        </div>
      </div>
  );
};

export default AdminCourseManagement;